import React, {useState} from 'react';
import {useSelector} from 'react-redux';
import {LEAGUE_CONFIGS} from '../../configs/leagues';
import {
  getRankedChestsByPlace,
  LEAGUE_NUMENS_MULTIPLIERS,
  LEAGUE_TO_CHEST,
  LOWER_LEAGUE_CHEST,
  NUMENS_BASE_REWARD,
  NUMENS_REWARD_PER_PLAYER_BELOW,
  NUMENS_WIN_POINTS_RATIO_LIMIT,
  RANKED_CHEST_PLAYER_COUNTS,
  RANKED_NUMENS_MULTIPLIER,
} from '../../configs/rankMatchRewards';
import {RANK_SKIN_LEAGUES, RANK_SKIN_MIN_PLAYERS} from '../../configs/rankSkins';
import {LEAGUE_DAILY_CRYSTALS, RATING_DAILY_PENALTY} from '../../configs/ratingCrystals';
import {getLeagueQuest} from '../../configs/quests';
import {LEAGUE_ADJECTIVES} from '../../utils/bots/getVisibleBotTitle';
import {pluralizeRu} from '../../utils/other/pluralizeRu';
import {getLeagueByRating} from '../../utils/rating/getLeagueByRating';
import {getChestIconPath} from '../../utils/inventory/getChestIconPath';
import {getPlaceIconPath} from '../../utils/gameEnd/getPlaceIconPath';
import {getChestDisplayName} from '../../utils/inventory/getChestDisplayName';
import {getNextGameDayStartMs} from '../../utils/date/getGameDayKey';
import {formatDayMonth} from '../../utils/date/formatDayMonth';
import {useIsDailyDoubleRewardAvailable} from '../../hooks/rewards/useIsDailyDoubleRewardAvailable';
import {selectServerTimeDelta} from '../../redux/reducers/privateStateReducer/selectors';
import {MENU_SOUND_MANAGER} from '../../scenes/MenuSoundManager';
import ChestContentsModal from '../Modals/ChestContentsModal/ChestContentsModal';
import MaskedIcon from '../coloredIcons/MaskedIcon/MaskedIcon';

// Места, для которых расписаны сундуки: на шестерых последнее с сундуком — пятое
const CHEST_TABLE_PLACES = [1, 2, 3, 4, 5];
const FIRST_DAILY_MATCH_MULTIPLIER = 2;
const PREMIUM_MULTIPLIER = 2;

const getLeagueShortName = (league) => league.name.replace(' лига', '');

// Сундуки клетки таблицы «место × число игроков»: подряд одинаковые — одним
// значком с числом
const groupChests = (chests) => chests.reduce((groups, chestId) => {
  const last = groups[groups.length - 1];
  if (last && last.chestId === chestId) {
    last.count += 1;
  } else {
    groups.push({chestId, count: 1});
  }
  return groups;
}, []);

/**
 * Награды рейтингового матча для экрана настройки поиска: от чего они
 * зависят и сколько дает выбранная лига матча (по умолчанию — лига игрока).
 * Числа берутся из тех же конфигов, по которым начисляет сервер
 * (configs/rankMatchRewards, ratingCrystals, rankSkins, quests). Нажатие на
 * сундук открывает модалку его содержимого.
 */
function RankMatchRewardsInfo() {
  const auth = useSelector((state) => state.private.auth);
  const [selectedLeagueId, setSelectedLeagueId] = useState(() => getLeagueByRating(auth.rating).id);
  const [openedChestId, setOpenedChestId] = useState(null);

  const league = LEAGUE_CONFIGS.find((config) => config.id === selectedLeagueId) || LEAGUE_CONFIGS[0];
  const currentChest = LEAGUE_TO_CHEST[league.id];
  const lowerChest = LOWER_LEAGUE_CHEST[league.id];
  const leagueMultiplier = LEAGUE_NUMENS_MULTIPLIERS[league.id] ?? 1;
  const dailyCrystals = LEAGUE_DAILY_CRYSTALS[league.id] || 0;
  const firstCrystalsLeague = LEAGUE_CONFIGS.find((config) => LEAGUE_DAILY_CRYSTALS[config.id]);
  const rankSkinLeagues = RANK_SKIN_LEAGUES
    .filter((leagueId) => LEAGUE_CONFIGS.find((config) => config.id === leagueId).minRating <= league.minRating);
  const firstRankSkinLeague = LEAGUE_CONFIGS.find((config) => config.id === RANK_SKIN_LEAGUES[0]);
  const leagueQuest = getLeagueQuest(league.id);
  const isLeagueQuestCompleted = Boolean(leagueQuest) && (auth.completedQuests || []).includes(leagueQuest.id);

  const handleSelectLeague = (leagueId) => {
    MENU_SOUND_MANAGER.playMenuButtonClick();
    setSelectedLeagueId(leagueId);
  };

  const renderChestButton = (chestId, caption, isMain) => (
    <button
      type="button"
      className={`rank-match-rewards_chest ${isMain ? 'rank-match-rewards_chest_main' : ''}`}
      onClick={() => setOpenedChestId(chestId)}
      title="Что может выпасть"
    >
      <img className="rank-match-rewards_chest-image" src={getChestIconPath(chestId)} alt={getChestDisplayName(chestId)}/>
      <span className="rank-match-rewards_chest-name">{getChestDisplayName(chestId)}</span>
      <span className="rank-match-rewards_chest-caption">{caption}</span>
    </button>
  );

  return (
    <div className="rank-match-rewards">
      <p className="rank-match-setup_text">
        Награды зависят от <b>лиги матча</b>, вашего <b>места</b> и <b>числа игроков</b>. Лига матча — по среднему
        рейтингу всех участников, поэтому она может отличаться от вашей.
      </p>

      <div className="rank-match-rewards_leagues" role="tablist" aria-label="Лига матча">
        {[...LEAGUE_CONFIGS].reverse().map((config) => (
          <button
            key={config.id}
            type="button"
            role="tab"
            aria-selected={config.id === league.id}
            className={`rank-match-rewards_league-tab ${config.id === league.id ? 'rank-match-rewards_league-tab_selected' : ''}`}
            onClick={() => handleSelectLeague(config.id)}
            title={config.name}
          >
            <img className="rank-match-rewards_league-tab-icon" src={config.iconPath} alt={config.name}/>
          </button>
        ))}
      </div>
      <h3 className="rank-match-rewards_league-title">{`Матч лиги «${getLeagueShortName(league)}»`}</h3>

      <div className="rank-match-rewards_block">
        <h4 className="rank-match-rewards_subtitle">Сундуки</h4>
        <div className="rank-match-rewards_chests">
          {renderChestButton(currentChest, 'за высокие места', true)}
          {renderChestButton(lowerChest, 'за места пониже', false)}
        </div>
        <table className="rank-match-rewards_table">
          <thead>
            <tr>
              {/* Угол без подписи: места видны по значкам в строках, число
                  игроков — по значкам бюста в шапке */}
              <th className="rank-match-rewards_table-corner" aria-label="Место"/>
              {/* Число игроков матча — цифрой со значком бюста; значок черный,
                  маской перекрашен в цвет подписи колонки */}
              {RANKED_CHEST_PLAYER_COUNTS.map((count) => (
                <th key={count} title={`${count} ${pluralizeRu(count, 'игрок', 'игрока', 'игроков')} в матче`}>
                  {count}
                  <MaskedIcon
                    src="/img/icons/bust.png"
                    color="currentColor"
                    className="rank-match-rewards_players-icon"
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {CHEST_TABLE_PLACES.map((place) => (
              <tr key={place}>
                <td>
                  <img
                    className="rank-match-rewards_place-icon"
                    src={getPlaceIconPath(place - 1)}
                    alt={`${place}-е место`}
                    title={`${place}-е место`}
                  />
                </td>
                {RANKED_CHEST_PLAYER_COUNTS.map((count) => {
                  if (place > count) {
                    return <td key={count} className="rank-match-rewards_table-cell_empty"/>;
                  }
                  const chests = getRankedChestsByPlace(place, count, currentChest, lowerChest);
                  return (
                    <td key={count}>
                      {chests.length === 0 ? '—' : (
                        <span className="rank-match-rewards_cell-chests">
                          {groupChests(chests).map(({chestId, count: chestCount}) => (
                            <span key={chestId} className="rank-match-rewards_cell-chest">
                              <img
                                className={`rank-match-rewards_cell-chest-image ${chestId === currentChest ? 'rank-match-rewards_cell-chest-image_main' : ''}`}
                                src={getChestIconPath(chestId)}
                                alt={getChestDisplayName(chestId)}
                                title={getChestDisplayName(chestId)}
                              />
                              {chestCount > 1 && `×${chestCount}`}
                            </span>
                          ))}
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        <p className="rank-match-setup_note">
          Сундук лиги матча — в золотой рамке.{' '}
          {/* Подсказка про модалку содержимого — цветом внимания, иначе ее не замечают */}
          <span className="text-attention">Нажмите на сундук сверху, чтобы посмотреть, что из него может выпасть.</span>
          {' '}Делящие место получают награду худшего места из разделенных.
        </p>
      </div>

      <div className="rank-match-rewards_block">
        {/* Кристаллы — выше нумен: это награда лиги, а не матча, и самая ценная
            валюта; заголовок цветом и значком кристаллов */}
        <h4 className="rank-match-rewards_subtitle text-crystals">
          Кристаллы каждый день
          <img className="rank-match-rewards_inline-icon" src="/img/icons/crystal.png" alt=""/>
        </h4>
        {dailyCrystals > 0 ? (
          <p className="rank-match-setup_text">
            Пока вы в этой лиге, в конце игровых суток вы получаете{' '}
            <span className="text-crystals">
              {`+${dailyCrystals}`}
              <img className="rank-match-rewards_inline-icon" src="/img/icons/crystal.png" alt="кристаллов"/>
            </span>
            {` и теряете ${RATING_DAILY_PENALTY} рейтинга.`}
          </p>
        ) : (
          <p className="rank-match-setup_text">
            {`Ежедневных кристаллов в этой лиге нет — они начисляются с лиги «${getLeagueShortName(firstCrystalsLeague)}».`}
          </p>
        )}
      </div>

      <div className="rank-match-rewards_block">
        <h4 className="rank-match-rewards_subtitle">
          Нумены
          <img className="rank-match-rewards_inline-icon" src="/img/icons/numens.png" alt=""/>
          <span className="text-coins">{`×${RANKED_NUMENS_MULTIPLIER * leagueMultiplier}`}</span>
        </h4>
        <p className="rank-match-setup_text">
          {`За место: ${NUMENS_BASE_REWARD} плюс ${NUMENS_REWARD_PER_PLAYER_BELOW} за каждого игрока ниже вас. Чем больше `}
          {`ваших очков победы по сравнению с соперниками, тем больше нуменов — до ×${1 + NUMENS_WIN_POINTS_RATIO_LIMIT}. `}
          {`В рейтинговом матче это умножается на ${RANKED_NUMENS_MULTIPLIER} и на множитель лиги матча `}
          (<span className="text-coins">{`×${leagueMultiplier}`}</span>). Без очков победы нуменов нет.
        </p>
      </div>

      <div className="rank-match-rewards_block">
        <h4 className="rank-match-rewards_subtitle">Рейтинговые образы</h4>
        {rankSkinLeagues.length > 0 ? (
          <p className="rank-match-setup_text">
            {`За единоличное 1-е место в матче от ${RANK_SKIN_MIN_PLAYERS} игроков — `}
            {rankSkinLeagues.map((leagueId) => LEAGUE_ADJECTIVES[leagueId].toLowerCase()).join(', ')}
            {' '}образ фракции, за которую вы играли. Нужно, чтобы и средняя лига соперников, и ваша лига после матча
            были не ниже лиги образа. Полученные образы повторно не выдаются.
          </p>
        ) : (
          <p className="rank-match-setup_text">
            {`Рейтинговые образы фракций выдаются за победу в матчах от лиги «${getLeagueShortName(firstRankSkinLeague)}».`}
          </p>
        )}
      </div>

      {leagueQuest && (
        <div className="rank-match-rewards_block">
          <h4 className="rank-match-rewards_subtitle">За достижение лиги</h4>
          <p className="rank-match-setup_text">
            {`Первый рейтинговый матч, завершенный с рейтингом от ${league.minRating}, — `}
            <span className="rank-match-rewards_quest-chests">
              {`${leagueQuest.reward.count} × `}
              <img
                className="rank-match-rewards_cell-chest-image"
                src={getChestIconPath(leagueQuest.reward.chestId)}
                alt=""
              />
              {getChestDisplayName(leagueQuest.reward.chestId).toLowerCase()}
            </span>
            {isLeagueQuestCompleted && <span className="rank-match-rewards_status rank-match-rewards_status_done">получено</span>}
          </p>
        </div>
      )}

      {openedChestId && (
        <ChestContentsModal chestId={openedChestId} onClose={() => setOpenedChestId(null)}/>
      )}
    </div>
  );
}

/**
 * Множители наград за рейтинговый матч и их статус у игрока: доступна ли
 * сегодня двойная награда за первый матч дня (и когда снова), действует ли
 * премиум. Стоит рядом с кнопкой «Начать поиск» — это то, что игрок получит
 * прямо сейчас.
 */
export function RankMatchRewardMultipliers() {
  const auth = useSelector((state) => state.private.auth);
  const serverTimeDelta = useSelector(selectServerTimeDelta);
  const isDailyDoubleRewardAvailable = useIsDailyDoubleRewardAvailable(auth.lastDoubleRewardAt);
  const isPremiumActive = Boolean(auth.premiumExpiresAt)
    && new Date(auth.premiumExpiresAt).getTime() > Date.now();
  const nextGameDayStart = new Date(getNextGameDayStartMs(Date.now() + serverTimeDelta));
  const nextGameDayTime = nextGameDayStart.toLocaleTimeString('ru-RU', {hour: '2-digit', minute: '2-digit'});

  return (
    <div className="rank-match-rewards_block">
      <ul className="rank-match-rewards_multipliers">
        <li>
          <span>
            <b>{`×${FIRST_DAILY_MATCH_MULTIPLIER}`}</b> к нуменам и сундукам за первый рейтинговый матч игровых суток
          </span>
          {isDailyDoubleRewardAvailable ? (
            <span className="rank-match-rewards_status rank-match-rewards_status_active">доступно сейчас</span>
          ) : (
            <span className="rank-match-rewards_status">{`снова с ${nextGameDayTime}`}</span>
          )}
        </li>
        <li>
          <span>
            <b>{`×${PREMIUM_MULTIPLIER}`}</b> к нуменам и сундукам любого сетевого матча с{' '}
            <span className="rank-match-rewards_premium">
              <img className="rank-match-rewards_inline-icon" src="/img/icons/premium.png" alt=""/>
              <span className="text-coins">Премиумом</span>
            </span>
          </span>
          {isPremiumActive ? (
            <span className="rank-match-rewards_status rank-match-rewards_status_active">
              {`активен до ${formatDayMonth(auth.premiumExpiresAt)}`}
            </span>
          ) : (
            <span className="rank-match-rewards_status">не активен</span>
          )}
        </li>
      </ul>
      <p className="rank-match-setup_note">
        Множители перемножаются: с <span className="text-coins">Премиумом</span>
        {` первый матч дня дает ×${FIRST_DAILY_MATCH_MULTIPLIER * PREMIUM_MULTIPLIER}.`}
      </p>
    </div>
  );
}

export default RankMatchRewardsInfo;
